/**
 * The factory store run as a store (21 Oct 2026): what the screens read and
 * send. One place for the query keys, so a kit, a count or a decision
 * refreshes every figure it moves — everything sits under ['factory'].
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  approvesFactory,
  type BomFamily,
  type FactoryApprovalRow,
  type FactoryBomSaveLine,
  type FactoryBomView,
  type FactoryCountOutcome,
  type FactoryCountScope,
  type FactoryCountView,
  type FactoryHealth,
  type FactoryBoxGroup,
  type FactoryBoxView,
  type FactoryHealthRow,
  type FactoryInboundRow,
  type FactoryInboundView,
  type FactoryItemBoxes,
  type FactoryLabel,
  type FactoryLineState,
  type FactoryPickList,
  type FactoryScanOutInput,
  type FactoryScanOutResult,
  type StockHealthKey,
} from '@bright/shared';
import { api } from '@/lib/api';
import { useActingUser } from '@/lib/cover';

export interface Paginated<T> {
  data: T[];
  meta: { page: number; pageSize: number; total: number; totalPages: number };
}

export type HealthPage = FactoryHealth & Paginated<FactoryHealthRow>;

export interface HealthParams {
  q?: string;
  category?: string;
  health?: StockHealthKey;
  due?: boolean;
  sort?: 'health' | 'name' | 'cover' | 'use';
  page: number;
  pageSize: number;
}

export interface CountSummary {
  id: string;
  title: string;
  status: 'OPEN' | 'CLOSED';
  startedAt: string;
  closedAt: string | null;
  by: string | null;
  lines: number;
  set: number;
  pending: number;
  notTaken: number;
}

export interface BomDraftOutcome {
  vehicles: number;
  days: number;
  written: Partial<Record<BomFamily, number>>;
  kept: BomFamily[];
}

/**
 * The owner's desks and Amit decide count gaps (owner, 21 Oct 2026: "You and
 * Amit both"). Read from the acting user, as the server reads request.user:
 * whoever covers Amit in the maker's book decides them while he is away.
 */
export function useApprovesFactory(): boolean {
  const user = useActingUser();
  return Boolean(user) && approvesFactory({ email: user!.email, roleKey: user!.role.key });
}

export function useFactoryHealth(params: HealthParams) {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== false));
  return useQuery({
    queryKey: ['factory', 'health', clean],
    queryFn: () => api.get<HealthPage>('/factory/health', clean as Record<string, string | number | boolean>),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useBoms() {
  return useQuery({ queryKey: ['factory', 'boms'], queryFn: () => api.get<FactoryBomView[]>('/factory/boms'), staleTime: 30_000 });
}

export function useLineState() {
  return useQuery({ queryKey: ['factory', 'line'], queryFn: () => api.get<FactoryLineState>('/factory/line'), staleTime: 30_000 });
}

export function usePickList(family: BomFamily | null, vehicles: number | null) {
  return useQuery({
    queryKey: ['factory', 'pick', family, vehicles],
    queryFn: () => api.get<FactoryPickList>('/factory/line/pick', { family: family!, vehicles: vehicles! }),
    enabled: Boolean(family) && vehicles !== null && vehicles > 0,
    placeholderData: keepPreviousData,
  });
}

export function useCounts() {
  return useQuery({
    queryKey: ['factory', 'counts'],
    queryFn: () => api.get<{ open: FactoryCountView | null; recent: CountSummary[] }>('/factory/counts'),
  });
}

export function useApprovals(enabled: boolean) {
  return useQuery({
    queryKey: ['factory', 'approvals'],
    queryFn: () => api.get<FactoryApprovalRow[]>('/factory/approvals'),
    enabled,
    staleTime: 30_000,
    refetchInterval: enabled ? 120_000 : false,
  });
}

function useFactoryMutation<TBody, TOut>(fn: (body: TBody) => Promise<TOut>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['factory'] }),
  });
}

export const useSaveBomLines = () =>
  useFactoryMutation(({ family, lines }: { family: BomFamily; lines: FactoryBomSaveLine[] }) => api.put<FactoryBomView>(`/factory/boms/${family}/lines`, { lines }));

export const useConfirmBom = () =>
  useFactoryMutation(({ family, onLine }: { family: BomFamily; onLine: number }) => api.post<FactoryBomView>(`/factory/boms/${family}/confirm`, { onLine }));

export const useDraftBoms = () => useFactoryMutation(() => api.post<BomDraftOutcome>('/factory/boms/draft'));

export const useStartCount = () =>
  useFactoryMutation((body: { scope: FactoryCountScope; category?: string | null; itemIds?: string[] }) => api.post<FactoryCountView>('/factory/counts', body));

export const useCloseCount = () => useFactoryMutation((id: string) => api.post<FactoryCountView>(`/factory/counts/${id}/close`));

export const useCountLine = () =>
  useFactoryMutation(({ lineId, counted, note }: { lineId: string; counted: number; note?: string | null }) =>
    api.post<FactoryCountOutcome>(`/factory/count-lines/${lineId}`, { counted, note: note ?? null }),
  );

export const useCorrectOnLine = () =>
  useFactoryMutation(({ family, onLine, note }: { family: BomFamily; onLine: number; note: string }) =>
    api.post<FactoryBomView>(`/factory/boms/${family}/on-line`, { onLine, note }),
  );

export const useDecideCount = () =>
  useFactoryMutation(({ lineId, approve, note }: { lineId: string; approve: boolean; note?: string | null }) =>
    api.post<{ status: 'APPROVED' | 'REJECTED' | 'SUPERSEDED' }>(`/factory/count-lines/${lineId}/decide`, { approve, note: note ?? null }),
  );

/** "12 days", "a day", "under a day", or the dash when the part is not used. */
export function coverWords(days: number | null): string {
  if (days === null) return '—';
  if (days <= 0) return 'under a day';
  if (days === 1) return 'a day';
  if (days > 365) return 'over a year';
  return `${days} days`;
}

/** The Badge tone each health reads in — always with its word beside it, never colour alone. */
export const HEALTH_TONE: Record<StockHealthKey, 'danger' | 'warning' | 'signal' | 'success' | 'neutral'> = {
  OUT: 'danger',
  SHORT: 'danger',
  LOW: 'warning',
  OK: 'success',
  IDLE: 'neutral',
};

/** The bar colour for a cover, by the same health. */
export const HEALTH_BAR: Record<StockHealthKey, string> = {
  OUT: 'bg-danger',
  SHORT: 'bg-danger',
  LOW: 'bg-warning',
  OK: 'bg-success',
  IDLE: 'bg-line-strong',
};

// ---------------------------------------------------------------------------
// Goods in, boxes and scanning (21 Oct 2026)
// ---------------------------------------------------------------------------

export type InboundListStatus = 'OPEN' | 'RECEIVED' | 'CANCELLED';

export function useInbounds(status: InboundListStatus | undefined, page: number) {
  return useQuery({
    queryKey: ['factory', 'inbound', 'list', status ?? 'all', page],
    queryFn: () => api.get<Paginated<FactoryInboundRow>>('/factory/inbound', { ...(status ? { status } : {}), page, pageSize: 30 }),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export function useInbound(id: string | null) {
  return useQuery({
    queryKey: ['factory', 'inbound', id],
    queryFn: () => api.get<FactoryInboundView>(`/factory/inbound/${id}`),
    enabled: Boolean(id),
  });
}

export const useUploadInbound = () =>
  useFactoryMutation((file: File) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return api.post<FactoryInboundView>('/factory/inbound', form);
  });

export interface InboundHeaderPatch {
  supplier?: string;
  supplierGstin?: string | null;
  invoiceNo?: string | null;
  invoiceDate?: string | null;
  note?: string | null;
}
export const useUpdateInbound = () =>
  useFactoryMutation(({ id, patch }: { id: string; patch: InboundHeaderPatch }) => api.patch<FactoryInboundView>(`/factory/inbound/${id}`, patch));
export const useCancelInbound = () => useFactoryMutation((id: string) => api.post<FactoryInboundView>(`/factory/inbound/${id}/cancel`));

export interface InboundLinePatch {
  description?: string;
  detail?: string | null;
  quantity?: number;
  unit?: string | null;
  itemId?: string | null;
}
export const useAddInboundLine = () =>
  useFactoryMutation(({ id, line }: { id: string; line: InboundLinePatch }) => api.post<FactoryInboundView>(`/factory/inbound/${id}/lines`, line));
export const useUpdateInboundLine = () =>
  useFactoryMutation(({ lineId, patch }: { lineId: string; patch: InboundLinePatch }) => api.patch<FactoryInboundView>(`/factory/inbound/lines/${lineId}`, patch));
export const useRemoveInboundLine = () => useFactoryMutation((lineId: string) => api.delete<FactoryInboundView>(`/factory/inbound/lines/${lineId}`));
export const useReceiveLine = () =>
  useFactoryMutation(({ lineId, quantity, boxes, note }: { lineId: string; quantity: number; boxes: FactoryBoxGroup[]; note?: string | null }) =>
    api.post<{ inbound: FactoryInboundView; codes: string[] }>(`/factory/inbound/lines/${lineId}/receive`, { quantity, boxes, note: note ?? null }),
  );
export const useNotCome = () => useFactoryMutation((lineId: string) => api.post<FactoryInboundView>(`/factory/inbound/lines/${lineId}/not-come`));
export const useUndoLine = () => useFactoryMutation((lineId: string) => api.post<FactoryInboundView>(`/factory/inbound/lines/${lineId}/undo`));

export function useBox(code: string | null) {
  return useQuery({
    queryKey: ['factory', 'box', code],
    queryFn: () => api.get<FactoryBoxView>(`/factory/boxes/${encodeURIComponent(code!)}`),
    enabled: Boolean(code),
    retry: false,
  });
}

export function useItemBoxes(itemId: string | null) {
  return useQuery({
    queryKey: ['factory', 'item-boxes', itemId],
    queryFn: () => api.get<FactoryItemBoxes>(`/factory/items/${itemId}/boxes`),
    enabled: Boolean(itemId),
  });
}

export const useStickersForStock = () =>
  useFactoryMutation(({ itemId, boxes }: { itemId: string; boxes: FactoryBoxGroup[] }) => api.post<{ codes: string[] }>(`/factory/items/${itemId}/boxes`, { boxes }));
export const useVoidBox = () => useFactoryMutation(({ code, note }: { code: string; note?: string | null }) => api.post<FactoryBoxView>(`/factory/boxes/${code}/void`, { note: note ?? null }));
export const useTakeOut = () => useFactoryMutation((body: FactoryScanOutInput) => api.post<FactoryScanOutResult>('/factory/take-out', body));

/** Which stickers to print: an invoice's, a line's, a part's, or these codes. */
export type StickerSelection = { inboundId: string } | { lineId: string } | { itemId: string } | { codes: string[] };

export function stickerSearch(selection: StickerSelection): string {
  const params = new URLSearchParams();
  if ('inboundId' in selection) params.set('inbound', selection.inboundId);
  else if ('lineId' in selection) params.set('line', selection.lineId);
  else if ('itemId' in selection) params.set('item', selection.itemId);
  else params.set('codes', selection.codes.join(','));
  return `?${params.toString()}`;
}

export function fetchLabels(selection: StickerSelection) {
  return api.post<{ labels: FactoryLabel[] }>('/factory/boxes/labels', selection);
}
